import React, { useEffect, useState } from 'react';
import { Box, Button, Popover, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  type IRemoteNotification,
  SuprSendFeedProvider,
  SuprSendProvider,
  useFeedClient,
  useSuprSendClient
} from '@suprsend/react';

import { fetchNotifications } from '@/adapters/fetchExaCare';
import NotificationToast from '@/components/ui/Toast/NotificationToast';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { useSuprSendJwtToken } from '@/hooks/useSuprSendJwtToken';

import {
  NOTIFICATION_FEED_OPTIONS,
  useNotificationFeed
} from '../hooks/useNotificationFeed';

import { MobileInboxWithNotifications } from './MobileInboxModal';
import { NotificationBellButton } from './NotificationBellButton';
import NotificationsPanel from './NotificationsPanel';
import {
  type Notification,
  notificationFromSuprSend
} from './notificationFromSuprSend';

interface SuprSendTokenPayload {
  token: string;
  expires_at: number;
}

const WORKSPACE_KEY = import.meta.env
  .VITE_SUPR_SEND_CLIENT_SIDE_API_KEY as string;
const TOAST_AUTO_HIDE_DELAY = 6000;

interface InboxModalProps {
  userId: string;
}

const NOTIFICATIONS_POPOVER_WIDTH = 470;
const NOTIFICATIONS_POPOVER_HEIGHT = 566;
const NOTIFICATIONS_POPOVER_VIEWPORT_MARGIN = 15;
const POPOVER_ARROW_WIDTH = 16;
const POPOVER_ARROW_HEIGHT = 8;
const POPOVER_ARROW_DEFAULT_CENTER = 32;

const PopoverArrow = styled(Box)({
  position: 'absolute',
  top: -POPOVER_ARROW_HEIGHT,
  width: POPOVER_ARROW_WIDTH,
  height: POPOVER_ARROW_HEIGHT,
  clipPath: 'polygon(50% 0, 100% 100%, 0 100%)',
  backgroundColor: 'var(--palette-semantic-background-base)'
});

const PopoverContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  borderRadius: 'inherit'
});

const DesktopNotificationsPopover: React.FC = () => {
  const { unreadCount } = useNotificationFeed();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [arrowCenter, setArrowCenter] = useState(POPOVER_ARROW_DEFAULT_CENTER);

  const openPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    const bell = event.currentTarget.getBoundingClientRect();
    const bellCenter = bell.left + bell.width / 2;
    const paperLeft = Math.max(
      NOTIFICATIONS_POPOVER_VIEWPORT_MARGIN,
      bellCenter - POPOVER_ARROW_DEFAULT_CENTER
    );
    setArrowCenter(bellCenter - paperLeft);
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => setAnchorEl(null);

  return (
    <>
      <NotificationBellButton badgeCount={unreadCount} onClick={openPopover} />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: POPOVER_ARROW_DEFAULT_CENTER
        }}
        marginThreshold={NOTIFICATIONS_POPOVER_VIEWPORT_MARGIN}
        slotProps={{
          paper: {
            sx: {
              width: NOTIFICATIONS_POPOVER_WIDTH,
              height: NOTIFICATIONS_POPOVER_HEIGHT,
              maxHeight: 'calc(100vh - 40px)',
              mt: `${POPOVER_ARROW_HEIGHT}px`,
              borderRadius: '8px',
              overflow: 'visible',
              boxShadow: 'none',
              filter: 'drop-shadow(0px 5px 15px rgba(117, 136, 153, 0.3))'
            }
          }
        }}
      >
        <PopoverArrow sx={{ left: arrowCenter - POPOVER_ARROW_WIDTH / 2 }} />
        <PopoverContent>
          <NotificationsPanel layout="popover" onClose={closePopover} />
        </PopoverContent>
      </Popover>
    </>
  );
};

interface ToastNotificationProps {
  onNewNotification: (data: IRemoteNotification) => void;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({
  onNewNotification
}) => {
  const feedClient = useFeedClient();

  useEffect(() => {
    if (!feedClient) {
      return;
    }

    if (!feedClient.emitter) {
      return;
    }

    feedClient.emitter.on('feed.new_notification', (data) => {
      onNewNotification(data);
    });

    return () => {
      if (feedClient.emitter) {
        feedClient.emitter.off('feed.new_notification');
      }
    };
  }, [feedClient, onNewNotification]);

  return null;
};

interface InboxWithNotificationsProps {
  userId: string;
  userToken?: string;
}

const InboxWithNotifications: React.FC<InboxWithNotificationsProps> = ({
  userId,
  userToken
}) => {
  const suprSendClient = useSuprSendClient();
  const [showToast, setShowToast] = useState(false);
  const [toastData, setToastData] = useState<Notification | null>(null);

  useEffect(() => {
    if (suprSendClient && userToken) {
      suprSendClient.identify(userId, userToken);
    }
  }, [suprSendClient, userId, userToken]);

  const handleNewNotification = (data: IRemoteNotification) => {
    setToastData(notificationFromSuprSend(data));
    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
      setToastData(null);
    }, TOAST_AUTO_HIDE_DELAY);
  };

  return (
    <SuprSendFeedProvider {...NOTIFICATION_FEED_OPTIONS}>
      <DesktopNotificationsPopover />
      <ToastNotification onNewNotification={handleNewNotification} />

      {showToast && toastData && (
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            minWidth: '380px'
          }}
        >
          <NotificationToast
            title={toastData.message.title}
            message={
              toastData.message.body +
              (toastData?.message?.subtext
                ? `\n${toastData.message.subtext}`
                : '')
            }
            actions={
              toastData?.message?.actions &&
              toastData.message.actions.length > 0 ? (
                <Stack
                  direction="row"
                  gap={1}
                  mt={1}
                  sx={{ paddingTop: '8px' }}
                >
                  {toastData.message.actions.map((action, index) => (
                    <Button
                      key={index}
                      size="small"
                      variant="contained"
                      color={index === 0 ? 'primary' : 'secondary'}
                      onClick={() => {
                        if (action.open_in_new_tab) {
                          window.open(action.url, '_blank');
                        } else {
                          window.location.href = action.url;
                        }
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Stack>
              ) : null
            }
            dismissToast={() => setShowToast(false)}
          />
        </Box>
      )}
    </SuprSendFeedProvider>
  );
};

const InboxModal: React.FC<InboxModalProps> = ({ userId }) => {
  const isMobile = useIsMobileViewport();
  const { data: tokenData, isLoading } = useSuprSendJwtToken(userId);

  if (isLoading) {
    if (isMobile) {
      return null;
    }
    return <NotificationBellButton badgeCount={0} />;
  }

  if (!tokenData?.token) {
    if (isMobile) {
      return null;
    }
    return <NotificationBellButton badgeCount={0} isDisabled />;
  }

  const handleRefreshToken = async () => {
    const response = await fetchNotifications.post<SuprSendTokenPayload>(
      `/suprsend/generate-jwt-token`,
      {
        user_id: userId,
        tenant_id: 'default',
        expires_in: 3600
      }
    );
    return response.token;
  };

  return (
    <SuprSendProvider
      publicApiKey={WORKSPACE_KEY}
      distinctId={userId}
      userToken={tokenData?.token}
      refreshUserToken={handleRefreshToken}
    >
      {isMobile ? (
        <MobileInboxWithNotifications />
      ) : (
        <InboxWithNotifications userId={userId} userToken={tokenData?.token} />
      )}
    </SuprSendProvider>
  );
};

export default InboxModal;
